import React, { useState, useMemo } from 'react'
import { StreamData } from 'types/streamData'
import styled from 'styled-components'
// @ts-ignore
import { useSysTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import { useTranslation } from 'react-i18next'
import ContentComponent from './ContentComponent'
import QuoteContent from './QuoteContent'
import DirectionContentComponent from '../DirectionContentComponent'

type PodcastContentProps = {
  streamData: StreamData
  highlighting?: boolean
}

const StyledTitle = styled.div<{ $theme: SystemTheme }>`
  font-size: ${({ $theme }) => $theme.typescale[14].size};
  line-height: ${({ $theme }) => $theme.typescale[14].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.semibold};
`

const StyledDivider = styled.div<{ $theme: SystemTheme }>`
  border-top: 1px solid ${({ $theme }) => $theme.common.divider[100]};
  margin-top: 10px;
  margin-bottom: 10px;
`

const PodcastContent = ({ streamData, highlighting }: PodcastContentProps) => {
  const theme = useSysTheme()
  const { t } = useTranslation()

  const [isDescriptionTextHidden, setIsDescriptionTextHidden] = useState<boolean>(true)
  const [isTranscriptTextHidden, setIsTranscriptTextHidden] = useState<boolean>(true)
  const showDescriptionHiddenText = () => {
    setIsDescriptionTextHidden(false)
  }
  const showTranscriptHiddenText = () => {
    setIsTranscriptTextHidden(false)
  }

  const {
    isDescriptionTruncated,
    isTranscriptTruncated,
    isDisplayTextRtl,
    descriptionDisplayText,
    descriptionHighlightedDisplayText,
    untruncatedDescriptionDisplayText,
    untruncatedDescriptionHighlightedDisplayText,
    transcriptDisplayText,
    transcriptHighlightedDisplayText,
    untruncatedTranscriptDisplayText,
    untruncatedTranscriptHighlightedDisplayText
  } = streamData
  const hasMoreDescriptionText = isDescriptionTextHidden && isDescriptionTruncated!

  const hasMoreTranscriptText = isTranscriptTextHidden && isTranscriptTruncated!

  const descriptionText = useMemo(() => {
    let text = highlighting ? descriptionHighlightedDisplayText! : descriptionDisplayText!
    if (!isDescriptionTextHidden) {
      text = highlighting
        ? untruncatedDescriptionHighlightedDisplayText!
        : untruncatedDescriptionDisplayText!
    }
    return text
  }, [
    descriptionHighlightedDisplayText,
    descriptionDisplayText,
    highlighting,
    isDescriptionTextHidden,
    untruncatedDescriptionDisplayText,
    untruncatedDescriptionHighlightedDisplayText
  ])

  const transcriptText = useMemo(() => {
    let text = highlighting ? transcriptHighlightedDisplayText! : transcriptDisplayText!
    if (!isTranscriptTextHidden) {
      text = highlighting
        ? untruncatedTranscriptHighlightedDisplayText!
        : untruncatedTranscriptDisplayText!
    }
    return text
  }, [
    transcriptHighlightedDisplayText,
    transcriptDisplayText,
    highlighting,
    isTranscriptTextHidden,
    untruncatedTranscriptDisplayText,
    untruncatedTranscriptHighlightedDisplayText
  ])

  return (
    <>
      <StyledDivider $theme={theme} style={{ marginTop: 0 }} />

      {descriptionText && (
        <>
          <DirectionContentComponent isRtl={isDisplayTextRtl}>
            <StyledTitle $theme={theme}>{t('episodeDescription')}</StyledTitle>
          </DirectionContentComponent>
          <DirectionContentComponent isRtl={isDisplayTextRtl}>
            <ContentComponent
              text={descriptionText}
              hasMoreText={hasMoreDescriptionText}
              showHiddenText={showDescriptionHiddenText}
            />
          </DirectionContentComponent>
          <StyledDivider $theme={theme} />
        </>
      )}

      <DirectionContentComponent isRtl={isDisplayTextRtl}>
        <StyledTitle $theme={theme}>{t('transcript')}</StyledTitle>
      </DirectionContentComponent>
      <DirectionContentComponent isRtl={isDisplayTextRtl}>
        <ContentComponent
          text={transcriptText}
          hasMoreText={hasMoreTranscriptText}
          showHiddenText={showTranscriptHiddenText}
        />
      </DirectionContentComponent>
      {streamData.parentText ? <QuoteContent streamData={streamData} /> : ''}
    </>
  )
}

export default PodcastContent
